/**
 * SmartTeacher Design System v2 — palette + brand helpers (web).
 *
 * Mirrors core/ui/theme/Colors.kt and core/ui/theme/Brand.kt on Android.
 * Single source of truth for class / course / category colour identity.
 *
 *   Black is primary. Hue is information, not decoration. Coral is a brand
 *   accent used in a very small set of moments (focus ring, selected row,
 *   brand surfaces). See Brand.kt for the full rule.
 */

export interface PaletteHue {
  /** Background tint — chip fill, avatar tint, soft surface. */
  readonly soft: string
  /** Picker swatch — vivid mid-tone, AA on ink.white. */
  readonly solid: string
  /** Text/icon colour sitting on .soft. */
  readonly ink: string
  readonly label: PaletteName
}

export type PaletteName =
  | 'indigo' | 'blue' | 'teal' | 'green' | 'lime' | 'amber'
  | 'orange' | 'red' | 'pink' | 'purple' | 'brown' | 'grey'

export const Palette: Record<PaletteName, PaletteHue> = {
  indigo: { soft: '#EEF0FB', solid: '#5562E8', ink: '#4A55C4', label: 'indigo' },
  blue:   { soft: '#E6EEF9', solid: '#2C7BE5', ink: '#2867C5', label: 'blue' },
  teal:   { soft: '#E0F2F0', solid: '#14A99C', ink: '#0F8B80', label: 'teal' },
  green:  { soft: '#E6F2E8', solid: '#34A84A', ink: '#2E8A3F', label: 'green' },
  lime:   { soft: '#EEF2DD', solid: '#8AAF18', ink: '#6E8A16', label: 'lime' },
  amber:  { soft: '#F8ECD2', solid: '#D68A0A', ink: '#A96A06', label: 'amber' },
  orange: { soft: '#F8E6DA', solid: '#EC6B25', ink: '#C25A1F', label: 'orange' },
  red:    { soft: '#F8E1E5', solid: '#E23E58', ink: '#C4344A', label: 'red' },
  pink:   { soft: '#F5E0EC', solid: '#DB4793', ink: '#B73D7C', label: 'pink' },
  purple: { soft: '#ECE0F4', solid: '#9050D4', ink: '#7A3FB5', label: 'purple' },
  brown:  { soft: '#EFE5DC', solid: '#9C6A44', ink: '#7A5436', label: 'brown' },
  grey:   { soft: '#ECECEF', solid: '#7A7A87', ink: '#5B5B66', label: 'grey' },
}

/** All 12 curated hues. For display/reference; indigo here is system-only. */
export const PaletteList: readonly PaletteHue[] = [
  Palette.indigo, Palette.blue, Palette.teal, Palette.green,
  Palette.lime, Palette.amber, Palette.orange, Palette.red,
  Palette.pink, Palette.purple, Palette.brown, Palette.grey,
]

/**
 * Class-identity rotation — 11 hues, indigo retired.
 *
 * Indigo is "the app" (canvas, rail active, primary), never "a class", so it
 * is excluded from the deterministic identity rotation. Same collision fix as
 * mobile. `Palette.indigo` itself stays available for system/chrome use.
 */
export const ClassPaletteList: readonly PaletteHue[] = [
  Palette.blue, Palette.teal, Palette.green, Palette.lime,
  Palette.amber, Palette.orange, Palette.red, Palette.pink,
  Palette.purple, Palette.brown, Palette.grey,
]

/**
 * Deterministic hue for a stable key (classId, studentId, courseId, …).
 *
 * Mirrors `Palette.forKey()` on Android: same JVM-style `String.hashCode()` →
 * same hue, so a class shows the same accent on web and mobile. Hashes over the
 * indigo-free class rotation, so a class is never assigned indigo.
 */
export function paletteForKey(key: string): PaletteHue {
  return ClassPaletteList[Math.abs(javaStringHashCode(key)) % ClassPaletteList.length]!
}

/** Java/Kotlin `String.hashCode()` — needed so web hashes match Android. */
function javaStringHashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = Math.imul(31, hash) + str.charCodeAt(i) | 0
  }
  return hash
}

export function paletteForIndex(index: number): PaletteHue {
  const len = ClassPaletteList.length
  return ClassPaletteList[((index % len) + len) % len]!
}

/**
 * `mix(hex, alpha)` — composite `hex` over white at `alpha` (0–1).
 *
 * Mirrors mobile's mix helper. Powers the class-tinted workspace scaffold
 * (§04): table header band `mix(class.solid, 0.10)`, zebra `mix(class.solid,
 * 0.025–0.035)`, and the soft→white header wash. Lets a faint class hue read
 * over neutral table greys without shouting. Returns a `#rrggbb` string.
 */
export function mix(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  const a = Math.min(1, Math.max(0, alpha))
  const over = (c: number) => Math.round(c * a + 255 * (1 - a))
  const toHex = (c: number) => c.toString(16).padStart(2, '0')
  return `#${toHex(over(r))}${toHex(over(g))}${toHex(over(b))}`
}

/** Legacy enum (PaletteColour on Android) → v2 hue. Keep call sites compiling. */
export type PaletteColour =
  | 'RED' | 'BLUE' | 'INDIGO' | 'GREEN' | 'LIME' | 'PURPLE'
  | 'ORANGE' | 'YELLOW' | 'PINK' | 'TEAL' | 'BROWN' | 'GREY'

export function paletteFromColour(c: PaletteColour): PaletteHue {
  switch (c) {
    case 'RED':    return Palette.red
    case 'BLUE':   return Palette.blue
    case 'INDIGO': return Palette.indigo
    case 'GREEN':  return Palette.green
    case 'LIME':   return Palette.lime
    case 'PURPLE': return Palette.purple
    case 'ORANGE': return Palette.orange
    case 'YELLOW': return Palette.amber
    case 'PINK':   return Palette.pink
    case 'TEAL':   return Palette.teal
    case 'BROWN':  return Palette.brown
    case 'GREY':   return Palette.grey
  }
}

/**
 * Semantic state hues — bound to palette so semantic surfaces and palette
 * swatches share tone.
 */
export const Semantic = {
  success: { soft: Palette.green.soft,  solid: Palette.green.solid,  ink: Palette.green.ink  },
  warning: { soft: Palette.amber.soft,  solid: Palette.amber.solid,  ink: Palette.amber.ink  },
  danger:  { soft: Palette.red.soft,    solid: Palette.red.solid,    ink: Palette.red.ink    },
  info:    { soft: Palette.indigo.soft, solid: Palette.indigo.solid, ink: Palette.indigo.ink },
  neutral: { soft: '#F4F4F6',           solid: '#A1A1AA',            ink: '#71717A'          },
  ai:      { soft: Palette.purple.soft, solid: Palette.purple.solid, ink: Palette.purple.ink },
} as const

/**
 * Brand — coral. Used sparingly: focus rings, selected row leading bar,
 * brand splash, success-toast leading bar, pro paywall pill/badge. Never
 * for CTAs or status. See `Brand.kt` for the full rule.
 */
export const Brand = {
  coral: '#ED4E2A',
  coralSoft: '#FDE6DF',
} as const

/**
 * Schedule-type accents — hue-coded, matches Android's
 * `aliases.schedule.{lesson|event|assignment|birthday}`.
 */
export const ScheduleAccents = {
  lesson:     { ink: Palette.indigo.ink, tint: Palette.indigo.soft },
  assignment: { ink: Palette.amber.ink,  tint: Palette.amber.soft  },
  event:      { ink: Palette.green.ink,  tint: Palette.green.soft  },
  birthday:   { ink: Palette.red.ink,    tint: Palette.red.soft    },
} as const

/**
 * Grade band — colour encodes the band, not good/bad (§01 §4). Pills use
 * `.soft` bg + `.ink` text. Thresholds: ≥80 A/B, ≥65 B, ≥50 C, ≥35 D, <35 F;
 * null → neutral grey + em-dash.
 */
export interface GradeBand {
  readonly soft: string
  readonly ink: string
  readonly letter: string
}
export function gradeBand(pct: number | null | undefined): GradeBand {
  if (pct == null) return { soft: Ink[100], ink: Ink[400], letter: '—' }
  if (pct >= 80) return { soft: Semantic.success.soft, ink: Semantic.success.ink, letter: 'A' }
  if (pct >= 65) return { soft: Semantic.info.soft, ink: Semantic.info.ink, letter: 'B' }
  if (pct >= 50) return { soft: Semantic.warning.soft, ink: Semantic.warning.ink, letter: 'C' }
  if (pct >= 35) return { soft: Palette.brown.soft, ink: Palette.brown.ink, letter: 'D' }
  return { soft: Semantic.danger.soft, ink: Semantic.danger.ink, letter: 'F' }
}

/** Ink scale — sometimes useful in TS for chart fills, canvas, etc. */
export const Ink = {
  black: '#0C0C0E',
  900: '#161618',
  800: '#27272A',
  700: '#3F3F46',
  600: '#52525B',
  500: '#71717A',
  400: '#A1A1AA',
  300: '#D4D4D8',
  200: '#E4E4E7',
  100: '#ECECEF',
  50:  '#F4F4F6',
  white: '#FFFFFF',
  hairline: '#ECECEF',
} as const
